import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MuzeChartRenderer } from '@mode/charts/util-muze';
import { clamp } from 'lodash';

@Component({
  selector: 'mode-filler-buster',
  templateUrl: './filler-buster.component.html',
  styleUrls: ['./filler-buster.component.scss'],
})
export class FillerBusterComponent implements OnInit, AfterViewInit, OnDestroy {
  // bindings
  @Input() token!: string;
  @Input() fontSizeMax!: number;
  @Input() fontSizeMin!: number;
  @Input() fontName!: string;
  @Input() content!: string;
  // local
  canvas: MuzeChartRenderer.MuzeCanvas;
  shouldAddLink: boolean;
  chartUrl: string;
  observer!: ResizeObserver;
  interval: ReturnType<typeof setInterval> | undefined = undefined;

  @ViewChild('fbContainer') element!: ElementRef;

  @HostListener('window:resize.fb', ['$event'])
  handleResizeFb() {
    this.updateUI.bind(this);
  }

  constructor(
    private changeDetector: ChangeDetectorRef,
    private window: Window,
    private ngZone: NgZone
  ) {
    this.chartUrl = '';
    this.shouldAddLink = false;
  }

  ngOnInit(): void {
    // Alamode was attempting to replace the span outside of the Angular lifecycle
    // which was causing inconsistent loading of the big value chart values.
    // By attaching an object to the window with the chartIds and their corresponding
    // URLs, we can check for those values from within the Angular lifecycle
    // to ensure that the links are consistently replaced and filter functionality
    // is intact after the chart values are updated
    const chartToken = this.token;
    this.ngZone.runOutsideAngular(() => {
      this.interval = setInterval(() => {
        const alaModeCharts = (this.window as any)['ALAMODE_CHARTS'];
        if (alaModeCharts && alaModeCharts[chartToken]) {
          this.shouldAddLink = true;
          this.chartUrl = alaModeCharts[chartToken];
          this.changeDetector.detectChanges();
          clearInterval(this.interval as ReturnType<typeof setInterval>);
        }
      }, 500);
    });
  }

  ngAfterViewInit(): void {
    this.initWatchers();
    this.initCSS();
    this.updateUI();
  }

  ngOnDestroy() {
    this.interval && clearInterval(this.interval);
    this.unbindResizeHandler();
  }

  initWatchers() {
    this.observer = new ResizeObserver((entries) => {
      this.updateUI();
    });

    this.observer.observe(this.element.nativeElement);
  }

  initCSS() {
    this.element.nativeElement.style.display = 'block';
    this.element.nativeElement.style.width = '100%';
    this.element.nativeElement.style.height = '100%';
  }

  updateUI() {
    const px = (num: number) => `${num}px`;
    this.updateFontSize(this.element, px(this.calculateFontSize()));
  }

  updateFontSize($elem: ElementRef, fontSize: string) {
    $elem.nativeElement.style.fontSize = fontSize;
  }

  calculateFontSize() {
    const fontSizeScalar = 1;
    const containerWidth = this.element.nativeElement.offsetWidth;
    const fontBase = this.measureFontBase(this.content, fontSizeScalar, this.fontName);
    const scaledFontSize = this.scaleFontSize(fontSizeScalar, fontBase, containerWidth);

    return clamp(scaledFontSize, this.fontSizeMin, this.fontSizeMax);
  }

  measureFontBase(text: string, fontSize: number, fontName: string): number {
    this.canvas = this.canvas || this.window.document.createElement('canvas');

    let fontBase = this.measureCanvasText(this.canvas, text, fontSize, fontName);

    // For very small text content, the canvas can get confused and return decimals
    // less than one or even zero. Ensure it's always at least 1.
    // See: https://github.com/mode/webapp/pull/9433
    if (!fontBase || fontBase < 1) {
      fontBase = 1;
    }
    return fontBase;
  }

  measureCanvasText(canvas: MuzeChartRenderer.MuzeCanvas, text: string, fontSize: number, fontName: string): number {
    const font = `${fontSize}px ${fontName}`;
    const cxt = canvas.getContext('2d');

    cxt.font = font;
    return cxt.measureText(text).width;
  }

  scaleFontSize(fontSize: number, fontBase: number, containerWidth: number): number {
    const ratio = fontSize / fontBase;
    const size = containerWidth * ratio;

    return size | 0;
  }

  unbindResizeHandler() {
    this.observer?.unobserve(this.element.nativeElement);
  }

  eventNS(name: string) {
    return name + '.fb';
  }
}
